import {
  AutocompleteGenericEntity,
  FetchAutocomplete,
  FetchAutocompleteProps,
} from '@australiangreens/ag-internal-components';
import { useState } from 'react';

import { CONTACT_STATUS_CODE_MAPPINGS } from '@/types';
import { FieldMetaData } from './types';

type SupportStatusCode = keyof typeof CONTACT_STATUS_CODE_MAPPINGS;

type EntityType = Omit<AutocompleteGenericEntity, 'id'> & {
  id: SupportStatusCode;
};

const PRELOADED_OPTIONS: EntityType[] = Object.entries(CONTACT_STATUS_CODE_MAPPINGS).map(
  ([key, val]) => ({
    id: key as SupportStatusCode,
    label: val,
  })
);

type SupporterStatusTFCProps = Omit<
  FetchAutocompleteProps<EntityType>,
  'value' | 'onChange' | 'preloadedOptions'
> & {
  onChange: (value: SupportStatusCode[], fieldMetaData: FieldMetaData) => void;
  /** IDs for Restart pre-fill (matched from preLoadedOptions by id) */
  initialValue?: SupportStatusCode[];
};

/**
 * Similar to FormWithFetchAutcompleteByLabelTFC but uses the id of type based
 * on CONTACT_STATUS_CODE_MAPPINGS
 */
export default function SupporterStatusTFC({
  onChange,
  initialValue,
  ...props
}: SupporterStatusTFCProps) {
  const [value, setValue] = useState<EntityType[]>(() =>
    initialValue?.length
      ? PRELOADED_OPTIONS.filter((o) => initialValue.includes(o.id))
      : []
  );

  const handleOnChange = (newValue: EntityType[]) => {
    setValue(newValue);

    const state = newValue.length === 0 ? 'empty' : 'valid';
    const fieldMetaData = { state, error: null } as FieldMetaData;

    onChange(
      newValue.map((x) => String(x.id) as SupportStatusCode),
      fieldMetaData
    );
  };

  return (
    <FetchAutocomplete
      onChange={handleOnChange}
      value={value}
      preLoadedOptions={PRELOADED_OPTIONS}
      textFieldColor="secondary"
      {...props}
    />
  );
}
